import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Split, RotateCcw, Zap } from "lucide-react";
import { formatCurrency } from "@/lib/format";

const emptyLine = () => ({ _k: (globalThis.crypto?.randomUUID?.() || `l-${Math.random().toString(36).slice(2)}`), amount: "", category: "", note: "" });

// Preset templates users can tap to seed the splits.
const PRESETS = [
  {
    name: "50/50 personal + reimbursable",
    icon: "🍽️",
    build: (parent) => {
      const half = Math.round((parent.amount * 50) / 100) / 1;
      return [
        { amount: half.toFixed(2), category: parent.category, note: "Personal" },
        { amount: (parent.amount - half).toFixed(2), category: "Reimbursable", note: "Work" },
      ];
    },
  },
  {
    name: "70/30 split",
    icon: "⚖️",
    build: (parent) => {
      const seventy = Math.round((parent.amount * 70) / 100 * 100) / 100;
      return [
        { amount: seventy.toFixed(2), category: parent.category, note: "" },
        { amount: (parent.amount - seventy).toFixed(2), category: "Reimbursable", note: "" },
      ];
    },
  },
  {
    name: "3-way even split",
    icon: "3️⃣",
    build: (parent) => {
      const each = Math.floor((parent.amount / 3) * 100) / 100;
      const remainder = Math.round((parent.amount - each * 2) * 100) / 100;
      return [
        { amount: each.toFixed(2), category: parent.category, note: "" },
        { amount: each.toFixed(2), category: parent.category, note: "" },
        { amount: remainder.toFixed(2), category: parent.category, note: "" },
      ];
    },
  },
];

export default function SplitTransactionDialog({ open, onClose, onDone, parent }) {
  const [lines, setLines] = useState(() => [emptyLine(), emptyLine()]);
  const [categories, setCategories] = useState({ expense: [], income: [], saving: [] });
  const [saving, setSaving] = useState(false);
  const [existingChildren, setExistingChildren] = useState([]);

  const catList = useMemo(() => {
    if (!parent) return [];
    return (categories[parent.type] || []).map((c) => c.name);
  }, [categories, parent]);

  // Reset state on open, load categories, load existing children if already split
  useEffect(() => {
    if (!open || !parent) return;
    (async () => {
      try {
        const [{ data: cats }, { data: all }] = await Promise.all([
          api.get("/categories"),
          parent.has_splits
            ? api.get("/transactions")
            : Promise.resolve({ data: [] }),
        ]);
        setCategories(cats || { expense: [], income: [], saving: [] });
        if (parent.has_splits) {
          const kids = (all || []).filter(
            (t) => t.split_parent_id === parent.id
          );
          setExistingChildren(kids);
        } else {
          setExistingChildren([]);
        }
      } catch (err) {
        console.warn("[SplitDialog] load failed", err?.message);
      }
    })();
    setLines([emptyLine(), emptyLine()]);
  }, [open, parent]);

  if (!parent) return null;

  const parentAmount = parseFloat(parent.amount) || 0;
  const sumLines = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const remaining = Math.round((parentAmount - sumLines) * 100) / 100;
  const valid = lines.length >= 2
    && lines.every((l) => parseFloat(l.amount) > 0 && l.category)
    && Math.abs(remaining) <= 0.01;

  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const patchLine = (i, patch) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const applyPreset = (preset) => {
    const built = preset.build({ ...parent, amount: parentAmount });
    // iter 72n — ensure preset-built lines carry stable keys so the React
    // list doesn't reuse form input state across reorders.
    setLines(built.map((l) => ({ _k: (globalThis.crypto?.randomUUID?.() || `l-${Math.random().toString(36).slice(2)}`), ...l })));
  };

  const balanceRemainder = () => {
    if (lines.length === 0) return;
    // Push the remainder onto the last line
    setLines((ls) => {
      const copy = [...ls];
      const last = copy[copy.length - 1];
      const cur = parseFloat(last.amount) || 0;
      copy[copy.length - 1] = {
        ...last,
        amount: (cur + remaining).toFixed(2),
      };
      return copy;
    });
  };

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const body = {
        splits: lines.map((l) => ({
          amount: parseFloat(l.amount),
          category: l.category,
          note: l.note || "",
        })),
      };
      await api.post(`/transactions/${parent.id}/split`, body);
      toast.success(`Split into ${lines.length} lines`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    } finally {
      setSaving(false);
    }
  };

  const undo = async () => {
    setSaving(true);
    try {
      await api.delete(`/transactions/${parent.id}/split`);
      toast.success("Split reversed");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    } finally {
      setSaving(false);
    }
  };

  const showUndo = parent.has_splits;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="split-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="w-5 h-5 text-[var(--brand-text)]" />
            {showUndo ? "This transaction is split" : "Split transaction"}
          </DialogTitle>
          <DialogDescription>
            {showUndo
              ? `Already split into ${existingChildren.length} lines. You can undo to reset it.`
              : `Divide ${formatCurrency(parentAmount, parent.currency)} across multiple categories. The original stays; children carry the real category totals so reports don't double-count.`}
          </DialogDescription>
        </DialogHeader>

        {showUndo ? (
          <>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {existingChildren.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between border border-[var(--divider)] rounded-md px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium">{c.category}</div>
                    {c.note && <div className="text-xs text-[var(--muted-text)]">{c.note}</div>}
                  </div>
                  <div className="font-mono-data">{formatCurrency(c.amount, c.currency)}</div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="split-close">
                Close
              </Button>
              <Button
                onClick={undo}
                disabled={saving}
                data-testid="split-undo"
                className="bg-[#B4283A] hover:bg-[#8f1f2e] text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Undo split
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Presets */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">
                Quick presets
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    data-testid={`split-preset-${p.name.split(" ")[0]}`}
                    onClick={() => applyPreset(p)}
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border border-[var(--divider)] hover:bg-[var(--hover)] hover:border-[var(--brand)] transition"
                  >
                    <span>{p.icon}</span>
                    <span>{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Lines */}
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {lines.map((line, i) => (
                <div
                  key={line._k || i}
                  className="grid grid-cols-[100px_1fr_1fr_28px] gap-2 items-end"
                >
                  <div>
                    {i === 0 && (
                      <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                        Amount
                      </Label>
                    )}
                    <Input
                      data-testid={`split-line-amount-${i}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={line.amount}
                      onChange={(e) => patchLine(i, { amount: e.target.value })}
                      className="h-9 font-mono-data"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    {i === 0 && (
                      <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                        Category
                      </Label>
                    )}
                    <Select
                      value={line.category}
                      onValueChange={(v) => patchLine(i, { category: v })}
                    >
                      <SelectTrigger
                        data-testid={`split-line-cat-${i}`}
                        className="h-9"
                      >
                        <SelectValue placeholder="Pick category" />
                      </SelectTrigger>
                      <SelectContent>
                        {catList.map((cn) => (
                          <SelectItem key={cn} value={cn}>{cn}</SelectItem>
                        ))}
                        {parent.type === "expense" && !catList.includes("Reimbursable") && (
                          <SelectItem value="Reimbursable">Reimbursable</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    {i === 0 && (
                      <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                        Note
                      </Label>
                    )}
                    <Input
                      data-testid={`split-line-note-${i}`}
                      value={line.note}
                      onChange={(e) => patchLine(i, { note: e.target.value })}
                      placeholder="e.g. Personal"
                      className="h-9"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(i)}
                    disabled={lines.length <= 2}
                    data-testid={`split-line-remove-${i}`}
                    className="h-9 w-9 p-0 text-[var(--muted-text)] hover:text-[#B4283A]"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t pt-3 border-[var(--divider)]">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addLine}
                data-testid="split-add-line"
                className="text-[var(--brand-text)]"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add line
              </Button>
              <div className="text-sm">
                <span className="text-[var(--muted-text)] mr-2">Remaining:</span>
                <span
                  className={`font-mono-data ${
                    Math.abs(remaining) <= 0.01
                      ? "text-green-700"
                      : "text-[#B4283A]"
                  }`}
                  data-testid="split-remaining"
                >
                  {formatCurrency(remaining, parent.currency)}
                </span>
                {Math.abs(remaining) > 0.01 && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={balanceRemainder}
                    className="ml-2 h-auto p-0 text-xs"
                    data-testid="split-balance"
                  >
                    <Zap className="w-3 h-3 mr-1" /> Balance last line
                  </Button>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="split-cancel">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!valid || saving}
                data-testid="split-submit"
                className="btn-brand"
              >
                {saving ? "Splitting…" : `Split into ${lines.length}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
